import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  IQuest,
  NextTimeStatus,
  OverlayMode,
  OverlayState,
  QuestPeriod,
  QuestTypes,
} from '../../daily-quests-page.types';
import { ChestIcon } from '../chest-icon/chest-icon';
import { CountdownOverlay } from '../countdown-overlay/countdown-overlay';
import { Fireworks } from '../fireworks/fireworks';
import { WeeklyStreakProgressBar } from '../weekly-streak-progress-bar/weekly-streak-progress-bar';
import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';

import { ConnectWallet } from 'shared/components/sections/connect-wallet/connect-wallet';
import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import { useQuestsStore } from 'shared/store/questsStore';

/**
 * Displays a single quest (title, subtitle, chest, progress) and manages UX around:
 *  - Claiming Daily quest (on click or auto-claim after wallet connect)
 *  - Showing a one-time "Congrats" fireworks overlay after a quest is claimed
 *  - Countdown overlay while a quest is in cooldown
 *
 * FIREWORKS BEHAVIOR
 * -----------------------------------
 * We show fireworks (Congrats overlay) *once per cycle* after a quest is claimed:
 *
 * 1) NON-DAILY QUESTS (DailyPlay, DailyWin, DailyPrizeCrate, WeeklyStreak)
 *    - These are usually auto-claimed elsewhere (server/chain), not via this component.
 *    - When the user visits the page *after* completion, the quest will be:
 *        status === Cooldown AND nextAvailableAt > Date.now()
 *      In that case, we show fireworks exactly once by using a per-cycle 'seen' flag:
 *        seenKey = "dq:seen:{chainId}:{address}:{questId}:{cycleId}"
 *      If seenKey is present, fireworks will NOT replay.
 *
 * 2) DAILY QUEST (QuestTypes.Daily)
 *    - Daily is claimed *locally* here (on click or after wallet connect auto-claim).
 *    - To avoid accidental double-play (e.g., re-renders/refetch), we gate fireworks on:
 *        justClaimedKey(chainId, address) === "claimed"
 *      After showing fireworks, we set the same 'seen' key to prevent replay, and
 *      we remove the "just claimed" latch.
 *
 * WHY TWO SIGNALS?
 * ----------------
 * - NON-DAILY: No local transaction; we rely on "Cooldown in the future" + seenKey to play once.
 * - DAILY: We do have a local transaction; we require the explicit just-claimed latch to avoid
 *          showing fireworks on unrelated re-fetches or after re-mounts.
 *
 * UX
 * ------------------
 * - Fireworks auto-hide after ~3s.
 * - We also play a MONEY SFX on Congrats.
 */

interface IQuestCardProps {
  quest: IQuest;
  onClaim: (id: string) => Promise<boolean>;
  pendingId?: string | null;
}

/** Daily-only "just claimed"
 * It helps manage the UI flow, especially around wallet connection and
 * ensuring the claim process is only triggered once per user action.
 */
const AUTO_CLAIM_DAILY_KEY = (chainId?: number, address?: string) =>
  `dq:autoClaimDaily:${chainId ?? 0}:${(address ?? '').toLowerCase()}`;

export const QuestCard = ({ quest, onClaim, pendingId }: IQuestCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [overlay, setOverlay] = useState<OverlayState>(OverlayMode.None);
  const [isShowConnect, setIsShowConnect] = useState(false);
  const navigate = useNavigate();
  const { chainId, address, isConnected } = useAccount();
  const walletClient = useWalletClient();
  const publicClient = usePublicClient();
  const { setSnapshotPoints, fetchUserBalances } = useQuestsStore();

  // ensures auto-claim runs at most once per mount
  const autoClaimStartedRef = useRef(false);

  // optimistic pending fills the gap before parent propagates pendingId
  const [optimisticPending, setOptimisticPending] = useState(false);

  const isDaily = useMemo(() => quest.id === QuestTypes.Daily, [quest.id]);

  const claimableByUser = useMemo(
    () => isDaily && quest.status === NextTimeStatus.Ready,
    [isDaily, quest.status],
  );

  const isDailyClaimPending = useMemo(
    () => isDaily && pendingId === QuestTypes.Daily,
    [isDaily, pendingId],
  );

  // unified busy flag for UI
  const isDailyBusy = useMemo(
    () => isDailyClaimPending || optimisticPending,
    [isDailyClaimPending, optimisticPending],
  );

  /** Per-cycle "seen" flag to prevent fireworks replay */
  const baseKey = useMemo(() => `${chainId}:${address}:${quest.id}`, [chainId, address, quest.id]);
  const justClaimedKey = useMemo(() => AUTO_CLAIM_DAILY_KEY(chainId, address), [chainId, address]);

  /**
   * cycleId = unique identifier for this quest's current window (usually nextAvailableAt)
   * Format: {nextAvailableAt} (timestamp in ms)
   * Example: 1697059200000
   */
  const cycleId = useMemo(
    () => (quest.nextAvailableAt && quest.nextAvailableAt > 0 ? String(quest.nextAvailableAt) : ''),
    [quest.nextAvailableAt],
  );

  /**
   * Per-cycle "seen" key to prevent fireworks replay (for non-daily quests)
   * Format: dq:seen:{baseKey}:{cycleId}
   * Example: dq:seen:1:0xabc...:dailyPlay:1697059200000
   */
  const seenKey = useMemo(
    () => (cycleId ? `dq:seen:${baseKey}:${cycleId}` : ''),
    [baseKey, cycleId],
  );

  const handleChestClick = useCallback(async () => {
    if (isDailyBusy) return;

    if (claimableByUser) {
      // If deps are ready, claim now; otherwise set the auto-claim flag
      const depsReady = Boolean(address && walletClient && publicClient);
      if (depsReady) {
        setOptimisticPending(true);
        try {
          const ok = await onClaim(QuestTypes.Daily);
          if (ok) {
            try {
              await fetchUserBalances(publicClient, address, true);
              localStorage.setItem(justClaimedKey, 'claimed');
            } catch {}
            setOverlay(OverlayMode.Congrats);
            return;
          }
        } finally {
          setOptimisticPending(false);
        }
      } else {
        try {
          localStorage.setItem(justClaimedKey, 'request');
        } catch {}
        if (!isConnected) setIsShowConnect(true);
      }
      return;
    }

    if (quest.href) {
      navigate(quest.href);
      return;
    }

    // not claimable -> just expand/collapse details
    setExpanded((v) => !v);
  }, [
    isDailyBusy,
    claimableByUser,
    address,
    walletClient,
    publicClient,
    onClaim,
    justClaimedKey,
    isConnected,
    quest.href,
  ]);

  const handleConnectWallet = useCallback(() => {
    if (!isConnected && isDaily && quest.status === NextTimeStatus.Ready) {
      try {
        localStorage.setItem(justClaimedKey, 'request');
      } catch {}
      setIsShowConnect(true);
    }
  }, [isConnected, isDaily, quest.status, justClaimedKey]);

  const showCountdown = useMemo(
    () => quest.status === NextTimeStatus.Cooldown && overlay === OverlayMode.None,
    [quest.status, overlay, chainId],
  );

  /**
   * AUTO-CLAIM DAILY AFTER CONNECT
   * --------------------------------
   * If user clicked while disconnected, we stored 'request'. Once connected and Ready,
   * perform the claim, then set 'claimed' so fireworks can be gated properly.
   */
  useEffect(() => {
    const wantsAutoClaim = (() => {
      try {
        return localStorage.getItem(justClaimedKey) === 'request';
      } catch {
        return false;
      }
    })();

    if (isConnected) setIsShowConnect(false);
    if (!wantsAutoClaim) return;
    if (!isDaily || isDailyClaimPending) return;
    if (quest.status !== NextTimeStatus.Ready) {
      try {
        localStorage.removeItem(justClaimedKey);
      } catch {}
      return;
    }

    const depsReady = Boolean(address && walletClient && publicClient);
    if (!depsReady || autoClaimStartedRef.current) return;
    autoClaimStartedRef.current = true;

    (async () => {
      setOptimisticPending(true);
      try {
        const ok = await onClaim(QuestTypes.Daily);
        if (ok) {
          try {
            await fetchUserBalances(publicClient, address, true);
            localStorage.setItem(justClaimedKey, 'claimed');
          } catch {}
        }
      } finally {
        try {
          localStorage.removeItem(justClaimedKey);
        } catch {}
        setOptimisticPending(false);
        autoClaimStartedRef.current = false;
      }
    })();
  }, [
    isConnected,
    isDaily,
    isDailyClaimPending,
    quest.status,
    address,
    walletClient,
    publicClient,
    onClaim,
    justClaimedKey,
  ]);

  /**
   * SHOW FIREWORKS ONCE PER CYCLE
   * ------------------------------
   * - NON-DAILY: show once when we detect Cooldown in the future and not yet seen.
   * - DAILY:     show once only if 'justClaimed' latch is set (prevents duplicate plays).
   * After showing, we mark 'seenKey' and (for Daily) clear the latch.
   */
  useEffect(() => {
    if (!cycleId) return;

    const alreadySeen = seenKey ? localStorage.getItem(seenKey) === '1' : false;
    const isFutureCooldown =
      quest.status === NextTimeStatus.Cooldown && (quest.nextAvailableAt ?? 0) > Date.now();

    const dailyJustClaimed = (() => {
      try {
        return localStorage.getItem(justClaimedKey) === 'claimed';
      } catch {
        return false;
      }
    })();
    if (overlay !== OverlayMode.None) return;

    const shouldPlay = !alreadySeen && isFutureCooldown && (!isDaily || dailyJustClaimed);

    if (!shouldPlay) return;

    setOverlay(OverlayMode.Congrats);
    try {
      if (seenKey) localStorage.setItem(seenKey, '1');
      if (isDaily) localStorage.removeItem(justClaimedKey);
    } catch {}
  }, [isDaily, quest.status, quest.nextAvailableAt, cycleId, seenKey, justClaimedKey]);

  // SFX + auto-hide
  useEffect(() => {
    if (overlay === OverlayMode.Congrats) {
      audioManager?.playSound?.(AUDIO_TYPES.MONEY);
      const t = setTimeout(() => setOverlay(OverlayMode.None), 3000);
      return () => clearTimeout(t);
    }
  }, [overlay]);

  return (
    <div
      className="relative flex items-center justify-between gap-8 py-3 sm:mb-6 sm:rounded-2xl sm:border sm:border-border sm:bg-violet/50 sm:p-5 sm:py-4"
      data-testid={quest.id}
    >
      <div className="relative min-w-0 flex-1">
        <div className="gradient-title mb-1 text-xl font-bold leading-7">{quest.title}</div>

        {quest.subtitle && (
          <p className="pr-6 text-md font-semibold leading-4 text-white-text">
            <Trans
              i18nKey={typeof quest.subtitle === 'string' ? quest.subtitle : String(quest.subtitle)}
              components={[<span className="text-yellow" />, <span className="text-yellow" />]}
            />
          </p>
        )}

        <div
          className={[
            'absolute -bottom-7 left-0 right-0 transition-all duration-300',
            expanded ? 'opacity-100' : 'opacity-0',
            'overflow-hidden',
          ].join(' ')}
        >
          {quest.description && (
            <p className="border-t border-dashed border-white-text/10 pr-6 pt-1 text-sm font-medium leading-4 text-white-text/50">
              {quest.description}
            </p>
          )}
        </div>

        {quest.frequency === QuestPeriod.Weekly && quest.progress && (
          <div className="mt-3">
            <WeeklyStreakProgressBar
              current={quest.progress.current}
              target={quest.progress.target}
            />
          </div>
        )}
      </div>

      <div
        onClick={!isConnected ? handleConnectWallet : handleChestClick}
        aria-disabled={isDailyBusy}
        aria-busy={isDailyBusy}
        className={[
          'relative flex select-none items-center',
          isDailyBusy ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
        ].join(' ')}
      >
        <ChestIcon
          variant={quest.accent}
          points={quest.points}
          quest={quest}
          disabled={isDailyBusy}
          isPrizeCrate={quest.isPrizeCrate}
        />
      </div>

      {overlay === OverlayMode.Congrats && <Fireworks />}

      {!isConnected && isShowConnect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5 backdrop-blur-sm">
          <ConnectWallet
            title={'common.connectWallet'}
            description={'pages.dailyQuests.connectDesc'}
          />
        </div>
      )}

      {showCountdown && (
        <CountdownOverlay
          nextAt={quest.nextAvailableAt && quest.nextAvailableAt > 0 ? quest.nextAvailableAt : 0}
        />
      )}
    </div>
  );
};
